import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import CaseStudyCard from '../components/CaseStudyCard';
import CtaSection from '../components/CtaSection';
import retailAI from '../assets/api_powered_retail.webp';

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  category: string;
  description: string;
  imageUrl: string;
}

const CaseStudiesPage: React.FC = () => {
  // Sample case studies data
  const allCaseStudies: CaseStudy[] = [
    {
      id: 'ai-powered-analytics',
      title: 'AI-Powered Analytics Platform for Retail Chain',
      client: 'Global Retail Inc.',
      category: 'AI & Analytics',
      description: 'Developed a custom analytics platform using machine learning to predict inventory needs and optimize supply chain operations, resulting in a 32% reduction in stockouts and 24% decrease in excess inventory.',
      imageUrl: retailAI
    },
    {
      id: 'mobile-banking-app',
      title: 'Mobile Banking Application with Biometric Authentication',
      client: 'FinTech Solutions',
      category: 'Mobile Development',
      description: 'Created a secure and user-friendly mobile banking application with advanced biometric authentication features, enabling seamless transactions and account management for over 2 million users.',
      imageUrl: retailAI
    },
    {
      id: 'cloud-migration',
      title: 'Healthcare Data Migration to Secure Cloud Infrastructure',
      client: 'Healthcare Innovations',
      category: 'Cloud Solutions',
      description: 'Migrated sensitive healthcare data to a secure cloud infrastructure while ensuring compliance with industry regulations, improving performance by 45% and reducing operational costs by 30%.',
      imageUrl: retailAI
    },
    {
      id: 'ecommerce-platform',
      title: 'Scalable E-Commerce Platform with AI-Powered Recommendations',
      client: 'Fashion Retailer',
      category: 'Web Development',
      description: 'Built a custom e-commerce platform with AI-powered product recommendations, resulting in a 28% increase in average order value and 15% improvement in customer retention.',
      imageUrl: retailAI
    },
    {
      id: 'iot-monitoring',
      title: 'IoT-Based Monitoring System for Manufacturing',
      client: 'Industrial Solutions',
      category: 'IoT & AI',
      description: 'Developed an IoT-based monitoring system that uses real-time data and predictive analytics to prevent equipment failures, reducing downtime by 35% and maintenance costs by 25%.',
      imageUrl: retailAI
    },
    {
      id: 'chatbot-customer-service',
      title: 'AI Chatbot for Enhanced Customer Service',
      client: 'Telecom Provider',
      category: 'AI & NLP',
      description: 'Implemented an AI-powered chatbot using natural language processing to handle customer inquiries, resolving 75% of support tickets without human intervention and improving customer satisfaction by 40%.',
      imageUrl: retailAI
    },
    {
      id: 'supply-chain-optimization',
      title: 'Supply Chain Optimization Platform',
      client: 'Logistics Company',
      category: 'AI & Analytics',
      description: 'Created a supply chain optimization platform using advanced analytics and machine learning to improve logistics efficiency, reducing delivery times by 22% and transportation costs by 18%.',
      imageUrl: retailAI
    },
    {
      id: 'virtual-reality-training',
      title: 'Virtual Reality Training Program for Employees',
      client: 'Energy Corporation',
      category: 'VR & Training',
      description: 'Developed a virtual reality training program for hazardous environment operations, reducing training time by 40% and improving safety incident metrics by 65%.',
      imageUrl: retailAI
    },
    {
      id: 'predictive-maintenance',
      title: 'Predictive Maintenance System for Fleet Management',
      client: 'Transportation Services',
      category: 'IoT & AI',
      description: 'Implemented a predictive maintenance system for a transportation fleet using IoT sensors and machine learning, preventing major breakdowns and extending vehicle lifespan by 30%.',
      imageUrl: retailAI
    }
  ];

  // Filtering functionality
  const categories = ['All', 'AI & Analytics', 'Mobile Development', 'Cloud Solutions', 'Web Development', 'IoT & AI', 'AI & NLP', 'VR & Training'];
  const [activeCategory, setActiveCategory] = useState('All');
  
  const filteredCaseStudies = activeCategory === 'All' 
    ? allCaseStudies 
    : allCaseStudies.filter(study => study.category === activeCategory);

  return (
    <>
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium mb-4 backdrop-blur-sm">
            Case Studies
          </span>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Our Success Stories
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Explore how we've helped businesses across industries solve complex challenges and achieve their goals with innovative technology solutions.
          </p>
        </div>
      </section>
      
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 overflow-x-auto">
            <div className="inline-flex space-x-2 pb-4 min-w-full">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                    activeCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCaseStudies.map((study, index) => (
              <CaseStudyCard
                key={study.id}
                id={study.id}
                title={study.title}
                client={study.client}
                category={study.category}
                description={study.description}
                imageUrl={study.imageUrl}
                featured={index === 0 && activeCategory === 'All'}
              />
            ))}
          </div>
          
          {filteredCaseStudies.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold mb-2 text-gray-900">No case studies found</h3>
              <p className="text-gray-600 mb-6">
                No case studies match the selected category. Please try another category.
              </p>
              <button
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md"
                onClick={() => setActiveCategory('All')}
              >
                View All Case Studies
              </button>
            </div>
          )}
        </div>
      </section>
      
      
      {/* Featured Case Study */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-1/2">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img 
                  src={retailAI}
                  alt="AI-Powered Analytics Platform" 
                  className="w-full object-cover"
                />
              </div>
            </div>
            <div className="lg:w-1/2">
              <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
                Featured Case Study
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                AI-Powered Analytics Platform
              </h2>
              <p className="text-gray-600 mb-6">
                Global Retail Inc., a leading retail chain with over 500 stores worldwide, was facing challenges with inventory management and supply chain optimization. They needed a solution to predict demand accurately, minimize stockouts, and reduce excess inventory.
              </p>
              <div className="space-y-4 mb-8">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">The Challenge</h3>
                  <p className="text-gray-600">
                    Inefficient inventory management leading to frequent stockouts and excess inventory, impacting customer satisfaction and increasing operational costs.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Our Solution</h3>
                  <p className="text-gray-600">
                    We developed a custom AI-powered analytics platform that uses machine learning to analyze historical sales data, seasonal trends, and external factors to predict demand with high accuracy.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">The Results</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <div className="mr-2 text-blue-600">•</div>
                      <span>32% reduction in stockouts</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-blue-600">•</div>
                      <span>24% decrease in excess inventory</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-blue-600">•</div>
                      <span>18% improvement in supply chain efficiency</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-blue-600">•</div>
                      <span>$4.2 million annual cost savings</span>
                    </li>
                  </ul>
                </div>
              </div>
              <a 
                href="/"
                className="inline-flex items-center text-blue-600 hover:text-indigo-600 font-medium"
              >
                Read Full Case Study
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <CtaSection
        title="Ready to Transform Your Business?"
        subtitle="Contact us today to discuss your project and discover how we can help you achieve your goals."
        buttonText="Get in Touch"
        buttonLink="/contact"
        sectionStyle="gradient"
      />
    </>
  );
};

export default CaseStudiesPage;